import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import { AvoButton } from '../Main/AvoButton';

export interface InteractiveModalProps {
  hide?: boolean;
  height?: string;
  width?: string;
  overflow?: string;
  // When set, the modal escapes its border-radius / max-width:90% / sized
  // container on mobile (≤640px) and fills the full viewport edge-to-edge.
  // Used by the trial onboarding surveys where the modal IS the whole
  // mobile experience; opt-in so existing modals stay centered cards.
  mobileFullscreen?: boolean;
  children?: React.ReactChild | React.ReactFragment;
}

const InteractiveModalContainer = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;

  &.hide {
    display: none;
  }
`;

const InteractiveModalCard = styled.div<{ mobileFullscreen?: boolean }>`
  display: flex;
  border-radius: 16px;
  background-color: #FFFFFF;
  justify-content: center;
  align-items: center;
  max-width: 90%;

  ${({ $mobileFullscreen }) => $mobileFullscreen && `
    @media only screen and (max-width: 640px) {
      max-width: 100%;
      width: 100vw;
      height: 100vh;
      border-radius: 0;
    }
  `}
`;

const InteractiveModalInnerContainer = styled.div<{ height: string, width: string, overflow: string, mobileFullscreen?: boolean }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  ${({ $overflow }) => ($overflow ? `overflow: ${$overflow}` : '')};
  padding: 25px 35px;
  @media only screen and (max-width: 600px) {
    padding: 25px 18px;
  }

  ${({ $mobileFullscreen }) => $mobileFullscreen && `
    @media only screen and (max-width: 640px) {
      width: 100vw;
      height: 100vh;
      /*
        Top padding clears the app's fixed React header (subdomain + bell
        + nav, ~70-80px tall) so the modal's title isn't hidden behind it.
        Bottom padding uses env(safe-area-inset-bottom) for iOS home-bar
        clearance; falls back to 0 on non-iOS browsers.
      */
      padding: 80px 16px env(safe-area-inset-bottom, 0px);
    }
  `}
`;

export const InteractiveModal = ({ overflow, hide, height = '250px', width = '500px', mobileFullscreen, children }: InteractiveModalProps) => (
  <InteractiveModalContainer className={classNames(hide && 'hide')}>
    <InteractiveModalCard $mobileFullscreen={mobileFullscreen}>
      <InteractiveModalInnerContainer $overflow={overflow} $height={height} $width={width} $mobileFullscreen={mobileFullscreen}>
        {children}
      </InteractiveModalInnerContainer>
    </InteractiveModalCard>
  </InteractiveModalContainer>
);

InteractiveModal.Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

InteractiveModal.ButtonContainer = styled.div<{
  justifyCenter?: boolean
}>`
  display: flex;
  justify-content: ${({ $justifyCenter }) => ($justifyCenter ? 'center' : 'flex-end')};
  width: 100%;
  @media only screen and (max-width: 600px) {
    justify-content: center;
  }
`;

InteractiveModal.ConfirmationButton = styled(AvoButton)`
  ${AvoButton.styles.md}
  ${AvoButton.styles.red}
  margin-left: 10px !important;
  @media only screen and (max-width: 600px) {
    padding: 10px 20px !important;
    margin-right: 5px;
    max-width: 62%;
    margin-left: 4px !important;
  }
`;

InteractiveModal.PaleGreenButton = styled(AvoButton)`
  ${AvoButton.styles.md}
  ${AvoButton.styles.paleGreen}
  margin-left: 10px !important;
  @media only screen and (max-width: 600px) {
    margin-left: 0 !important;
    width: 50% !important;
  }
`;

InteractiveModal.GreenButton = styled(AvoButton)`
  ${AvoButton.styles.md}
  ${AvoButton.styles.brightGreen}
  margin-left: 10px !important;
`;

InteractiveModal.Button = styled(AvoButton)`
  ${AvoButton.styles.md}
  color: #ffffff !important;
  background-color: #bdbdbd;
  margin-left: 10px !important;
  max-width: 165px;
  @media only screen and (max-width: 600px) {
    width: 50% !important;
  }
`;

InteractiveModal.LimeButton = styled(AvoButton)`
  ${AvoButton.styles.md}
  ${AvoButton.styles.lime}
  margin-left: 10px !important;
  @media only screen and (max-width: 600px) {
    margin-left: 0 !important;
    width: 50% !important;
  }
`;
